import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Truck, ShoppingBag, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const SKUS = [
  {
    id: "pack-5",
    qty: 5,
    price: 9,
    perCode: 1.8,
    tag: null,
    blurb: "Starter pack — fits a small household.",
  },
  {
    id: "pack-10",
    qty: 10,
    price: 16,
    perCode: 1.6,
    tag: "BEST VALUE",
    blurb: "Save $2 vs two starter packs.",
  },
];

const PurchaseModal = ({ open, sku, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSubmit = useMemo(
    () =>
      form.name.trim() &&
      form.street.trim() &&
      form.city.trim() &&
      form.state.trim() &&
      form.zip.trim().length >= 4,
    [form]
  );

  const reset = () => {
    setForm({ name: "", street: "", city: "", state: "", zip: "" });
    setDone(false);
    setBusy(false);
  };

  const close = () => {
    onClose?.();
    setTimeout(reset, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || !sku) return;
    setBusy(true);
    // Mocked checkout — no backend yet.
    await new Promise((r) => setTimeout(r, 700));
    setBusy(false);
    setDone(true);
    toast.success(`Order placed (demo) — ${sku.qty} QR stickers on the way`);
  };

  return (
    <AnimatePresence>
      {open && sku && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-md flex items-end sm:items-center justify-center"
          data-testid="qr-purchase-modal-overlay"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-6 sm:m-4 max-h-[90vh] overflow-y-auto"
            data-testid="qr-purchase-modal"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="w-12 h-12 rounded-2xl brand-gradient-bg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <button
                onClick={close}
                data-testid="qr-purchase-close-btn"
                className="text-[#8A93A6] p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!done ? (
              <>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#277A82]">
                  Sticker pack — {sku.qty} codes
                </div>
                <h2 className="font-heading text-2xl font-bold text-[#2D3142] mt-1">
                  Where should we ship them?
                </h2>
                <p className="text-sm text-[#4F5D75] mt-2 leading-relaxed">
                  ${sku.price}.00 + free US shipping. Permanent, waterproof, dishwasher-safe stickers — slap one on every Tupperware.
                </p>

                <form onSubmit={submit} className="mt-5 space-y-3" data-testid="qr-purchase-form">
                  <div>
                    <Label htmlFor="ship-name" className="text-xs text-[#4F5D75]">Full name</Label>
                    <Input
                      id="ship-name"
                      data-testid="ship-name-input"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Doe"
                      className="bg-white border-[#E2E0D8] mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ship-street" className="text-xs text-[#4F5D75]">Street address</Label>
                    <Input
                      id="ship-street"
                      data-testid="ship-street-input"
                      value={form.street}
                      onChange={update("street")}
                      placeholder="123 Fridge Ln, Apt 4"
                      className="bg-white border-[#E2E0D8] mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <Label htmlFor="ship-city" className="text-xs text-[#4F5D75]">City</Label>
                      <Input
                        id="ship-city"
                        data-testid="ship-city-input"
                        value={form.city}
                        onChange={update("city")}
                        placeholder="Brooklyn"
                        className="bg-white border-[#E2E0D8] mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ship-state" className="text-xs text-[#4F5D75]">State</Label>
                      <Input
                        id="ship-state"
                        data-testid="ship-state-input"
                        value={form.state}
                        onChange={update("state")}
                        placeholder="NY"
                        maxLength={2}
                        className="bg-white border-[#E2E0D8] mt-1 uppercase"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ship-zip" className="text-xs text-[#4F5D75]">ZIP</Label>
                    <Input
                      id="ship-zip"
                      data-testid="ship-zip-input"
                      value={form.zip}
                      onChange={update("zip")}
                      placeholder="11201"
                      className="bg-white border-[#E2E0D8] mt-1"
                    />
                  </div>

                  <div className="tup-card p-4 bg-[#F5F3ED] mt-2" data-testid="qr-purchase-summary">
                    <div className="flex justify-between text-sm text-[#4F5D75]">
                      <span>{sku.qty} permanent QR stickers</span>
                      <span className="font-semibold text-[#2D3142]">${sku.price}.00</span>
                    </div>
                    <div className="flex justify-between text-xs text-[#8A93A6] mt-1">
                      <span>Shipping</span>
                      <span>Free</span>
                    </div>
                    <div className="border-t border-[#E2E0D8] mt-3 pt-3 flex justify-between">
                      <span className="font-heading font-semibold text-[#2D3142]">Total</span>
                      <span className="font-heading font-bold text-[#1F5566]">${sku.price}.00</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={!canSubmit || busy}
                    data-testid="qr-purchase-submit-btn"
                    className="w-full brand-gradient-bg text-white h-12 hover:opacity-90 mt-2"
                  >
                    {busy ? "Placing order…" : `Place order — $${sku.price}.00`}
                  </Button>
                  <p className="text-[10px] text-center text-[#8A93A6]">
                    Demo checkout — Stripe + real fulfillment coming soon. No card will be charged.
                  </p>
                </form>
              </>
            ) : (
              <div className="text-center py-4" data-testid="qr-purchase-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F0E5] flex items-center justify-center">
                  <Check className="w-7 h-7 text-[#4A8A78]" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-[#2D3142] mt-4">
                  Order placed (demo)
                </h2>
                <p className="text-sm text-[#4F5D75] mt-2 leading-relaxed">
                  Your <strong>{sku.qty}-pack</strong> of permanent QR stickers will ship to{" "}
                  <strong>{form.name}</strong> at <strong>{form.street}</strong>.
                </p>
                <div className="tup-card p-4 bg-[#F5F3ED] mt-5 text-left">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[#277A82]">
                    <Truck className="w-3.5 h-3.5" /> Estimated arrival
                  </div>
                  <div className="font-heading text-lg font-semibold text-[#2D3142] mt-1">
                    3–5 business days
                  </div>
                </div>
                <Button
                  onClick={close}
                  data-testid="qr-purchase-done-btn"
                  className="w-full mt-5 brand-gradient-bg text-white h-12 hover:opacity-90"
                >
                  Done
                </Button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const QRStickerPurchaseCard = () => {
  const [openSku, setOpenSku] = useState(null);
  const navigate = useNavigate();

  return (
    <>
      {/* BOX 1 — paid SKUs (top) */}
      <div className="mt-4 tup-card p-5" data-testid="qr-sticker-paid-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#DCEDF0] flex items-center justify-center shrink-0">
            <ShoppingBag className="w-5 h-5 text-[#1F5566]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
              Order pre-printed
            </div>
            <div className="font-heading text-lg font-semibold text-[#2D3142]">
              QR sticker packs
            </div>
          </div>
        </div>

        <p className="text-xs text-[#4F5D75] mt-3 leading-relaxed">
          Permanent, dishwasher-safe stickers shipped to your door. Peel, stick on any container, scan and you're tracking.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3" data-testid="qr-sticker-skus">
          {SKUS.map((sku) => (
            <button
              key={sku.id}
              onClick={() => setOpenSku(sku)}
              data-testid={`qr-sku-${sku.id}`}
              className={`relative text-left rounded-2xl p-4 border-2 transition-all hover:-translate-y-0.5 ${
                sku.tag
                  ? "border-[#277A82] bg-gradient-to-br from-white to-[#DCEDF0]"
                  : "border-[#E2E0D8] bg-white hover:border-[#277A82]"
              }`}
            >
              {sku.tag && (
                <div className="absolute -top-2 left-3 px-2 py-0.5 brand-gradient-bg text-white text-[9px] font-bold rounded-full">
                  {sku.tag}
                </div>
              )}
              <div className="flex items-baseline gap-1">
                <div className="font-heading text-2xl font-bold text-[#1F5566]">${sku.price}</div>
                <div className="text-[10px] text-[#8A93A6]">USD</div>
              </div>
              <div className="text-sm font-semibold text-[#2D3142] mt-1.5">
                {sku.qty} QR codes
              </div>
              <div className="text-[10px] text-[#8A93A6] mt-0.5">
                ~${sku.perCode.toFixed(2)} per sticker
              </div>
              <div className="text-[11px] text-[#4F5D75] mt-2 leading-snug">{sku.blurb}</div>
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2 text-[11px] text-[#8A93A6]">
          <Truck className="w-3.5 h-3.5" />
          <span>Free US shipping · 3–5 business days</span>
        </div>
      </div>

      {/* BOX 2 — free print-at-home (bottom, same visual weight as paid box above) */}
      <button
        type="button"
        onClick={() => navigate("/print-stickers")}
        data-testid="qr-sticker-print-card"
        className="mt-4 tup-card p-5 w-full text-left hover:bg-[#F5F3ED] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
            <Printer className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
              Print at home
            </div>
            <div className="font-heading text-lg font-semibold text-[#2D3142]">
              Print your own — free
            </div>
          </div>
        </div>

        <p className="text-xs text-[#4F5D75] mt-3 leading-relaxed">
          Download a ready-to-print PDF. Choose 1, 3, 5, 7 or 10 stickers on a single US Letter sheet — print on plain paper or any 8.5×11 adhesive label sheet.
        </p>

        <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#277A82]">
          Open the print sheet →
        </div>
      </button>

      <PurchaseModal
        open={Boolean(openSku)}
        sku={openSku}
        onClose={() => setOpenSku(null)}
      />
    </>
  );
};
